// src/pages/customer-panel/certificates/CertificateDetailPage.js
// Single certificate view page

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Divider,
    Tooltip,
    IconButton,
    CircularProgress,
    Alert,
    Snackbar,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import VerifiedIcon from '@mui/icons-material/Verified';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

export default function CertificateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    const fetchCertificate = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get(`/lms/portal/certificates/${id}`);
            const data = response.data?.certificate || response.data?.data || response.data;
            setCertificate(data);
        } catch (err) {
            console.error('Failed to fetch certificate:', err);
            setError(err.response?.data?.message || 'Failed to load certificate details.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchCertificate();
    }, [fetchCertificate]);

    const handleCopyNumber = useCallback(() => {
        const certNumber = certificate?.certificate_number || certificate?.cert_id;
        if (certNumber && navigator.clipboard) {
            navigator.clipboard.writeText(certNumber);
            setSnackbar({ open: true, message: 'Certificate number copied to clipboard!', severity: 'success' });
        }
    }, [certificate]);

    const handleDownload = useCallback(() => {
        const downloadUrl = certificate?.download_url || certificate?.pdf_url;
        if (downloadUrl) {
            window.open(downloadUrl, '_blank');
        } else {
            setSnackbar({ open: true, message: 'Download not available at this time.', severity: 'info' });
        }
    }, [certificate]);

    const handleShare = useCallback(() => {
        const shareUrl = certificate?.share_url || certificate?.verification_url || window.location.href;
        if (navigator.share) {
            navigator.share({
                title: `Certificate - ${certificate?.course_name || certificate?.title}`,
                url: shareUrl,
            }).catch(() => {});
        } else if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl);
            setSnackbar({ open: true, message: 'Share link copied to clipboard!', severity: 'success' });
        }
    }, [certificate]);

    const handleVerify = useCallback(() => {
        const verifyUrl = certificate?.verification_url;
        if (verifyUrl) {
            window.open(verifyUrl, '_blank');
        } else {
            setSnackbar({ open: true, message: 'Verification link not available.', severity: 'info' });
        }
    }, [certificate]);

    // Loading
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    // Error
    if (error) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Alert
                    severity="error"
                    sx={{ borderRadius: '12px', fontFamily, mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchCertificate} sx={{ fontFamily, textTransform: 'none' }}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/certificates')}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                >
                    Back to Certificates
                </Button>
            </Box>
        );
    }

    if (!certificate) return null;

    const certNumber = certificate.certificate_number || certificate.cert_id || 'N/A';
    const courseName = certificate.course_name || certificate.course_title || certificate.title || 'Course Certificate';
    const studentName = certificate.student_name || certificate.user_name ||
        (user ? `${user.profile?.fname || ''} ${user.profile?.lname || ''}`.trim() : 'Student');
    const completionDate = formatDate(certificate.completion_date || certificate.issue_date || certificate.issued_at || certificate.created_at);
    const issuer = certificate.issuer || certificate.issued_by || certificate.organization || 'Learning Platform';
    const isValid = !certificate.status || certificate.status === 'valid' || certificate.status === 'active';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 4, px: { xs: 2, md: 4 } }}>
            {/* Back button */}
            <Box sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/customer-panel/certificates')}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: '#64748b' }}
                >
                    Back to Certificates
                </Button>
            </Box>

            {/* Certificate Card */}
            <Paper
                elevation={0}
                sx={{
                    maxWidth: 800,
                    mx: 'auto',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: '2px solid #e2e8f0',
                    position: 'relative',
                }}
            >
                {/* Decorative top border */}
                <Box
                    sx={{
                        height: 8,
                        background: `linear-gradient(90deg, #f59e0b 0%, ${ACCENT} 50%, #3b82f6 100%)`,
                    }}
                />

                {/* Certificate body */}
                <Box sx={{ p: { xs: 3, md: 5 }, textAlign: 'center', position: 'relative' }}>
                    {/* Decorative corner borders */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 20,
                            left: 20,
                            right: 20,
                            bottom: 20,
                            border: '2px solid #f1f5f9',
                            borderRadius: '16px',
                            pointerEvents: 'none',
                        }}
                    />

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {/* Award icon */}
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                boxShadow: '0 8px 24px rgba(245,158,11,0.3)',
                            }}
                        >
                            <WorkspacePremiumIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>

                        {/* Title */}
                        <Typography
                            variant="overline"
                            sx={{
                                fontFamily,
                                letterSpacing: 4,
                                color: '#94a3b8',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                            }}
                        >
                            CERTIFICATE OF COMPLETION
                        </Typography>

                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily,
                                fontWeight: 800,
                                color: '#1e293b',
                                mt: 1.5,
                                mb: 1,
                                fontSize: { xs: '1.4rem', md: '1.8rem' },
                            }}
                        >
                            {courseName}
                        </Typography>

                        {/* Status chip */}
                        <Chip
                            icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                            label={isValid ? 'Verified' : (certificate.status || 'Expired')}
                            size="small"
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                bgcolor: isValid ? '#dcfce7' : '#fee2e2',
                                color: isValid ? '#166534' : '#991b1b',
                                '& .MuiChip-icon': { color: isValid ? '#166534' : '#991b1b' },
                                mb: 3,
                            }}
                        />

                        <Divider sx={{ my: 3, mx: { xs: 0, md: 6 } }} />

                        {/* Recipient */}
                        <Typography
                            sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', mb: 0.5 }}
                        >
                            This certifies that
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily,
                                fontWeight: 700,
                                color: ACCENT,
                                mb: 0.5,
                                fontSize: { xs: '1.2rem', md: '1.5rem' },
                            }}
                        >
                            {studentName}
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem', mb: 3 }}>
                            has successfully completed the above course
                        </Typography>

                        {/* Details grid */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: { xs: 2, md: 4 },
                                flexWrap: 'wrap',
                                mb: 3,
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mb: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                        Completion Date
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                                    {completionDate}
                                </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mb: 0.5 }}>
                                    <SchoolIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                        Issued By
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                                    {issuer}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Certificate number */}
                        <Paper
                            elevation={0}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2.5,
                                py: 1,
                                borderRadius: '12px',
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                            }}
                        >
                            <QrCode2Icon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    color: '#475569',
                                    fontFeatureSettings: '"tnum"',
                                    letterSpacing: 0.5,
                                }}
                            >
                                {certNumber}
                            </Typography>
                            <Tooltip title="Copy certificate number">
                                <IconButton size="small" onClick={handleCopyNumber}>
                                    <ContentCopyIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </Box>
                </Box>

                {/* Decorative bottom border */}
                <Box
                    sx={{
                        height: 8,
                        background: `linear-gradient(90deg, #3b82f6 0%, ${ACCENT} 50%, #f59e0b 100%)`,
                    }}
                />
            </Paper>

            {/* Action buttons */}
            <Box
                sx={{
                    maxWidth: 800,
                    mx: 'auto',
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        bgcolor: ACCENT,
                        borderRadius: '12px',
                        px: 3,
                        py: 1.2,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    Download PDF
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={handleShare}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: '#e2e8f0',
                        color: '#475569',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.2,
                        '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                    }}
                >
                    Share
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<VerifiedIcon />}
                    onClick={handleVerify}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: '#e2e8f0',
                        color: '#475569',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.2,
                        '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                    }}
                >
                    Verify
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '12px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
